import * as React from 'react';
import TableEditPlugin from '../plugin/TableEditPlugin';
import TableEditViewState from '../store/schema/TableEditViewState';
import setTableEditMenuViewState from '../actions/setTableEditMenuViewState';
import strings from 'owa-strings/owa-mail-strings';
import { ContextualMenu } from 'office-ui-fabric-react/lib/ContextualMenu';
import { IContextualMenuItem } from 'office-ui-fabric-react/lib/ContextualMenu';
import { TableOperation } from 'roosterjs-editor-types';
import { computed } from 'mobx';
import { observer } from 'mobx-react';

export interface TableEditContextMenuProps {
    viewState: TableEditViewState;
    plugin: TableEditPlugin;
}

@observer
export default class TableEditContextMenu extends React.Component<TableEditContextMenuProps, {}> {
    render() {
        let { position } = this.props.viewState;
        return (
            position && (
                <ContextualMenu
                    shouldFocusOnMount={true}
                    target={position}
                    onDismiss={this.onDismiss}
                    items={this.menuItems}
                />
            )
        );
    }

    private onDismiss = () => {
        setTableEditMenuViewState(this.props.viewState, null, this.props.viewState.hasHeaders);
    };

    @computed
    private get menuItems(): IContextualMenuItem[] {
        return [
            {
                key: 'insert',
                name: strings.tableMenuInsert,
                subMenuProps: {
                    items: [
                        {
                            key: 'insertAbove',
                            name: strings.tableMenuInsertAbove,
                            data: TableOperation.InsertAbove,
                            onClick: this.editTable,
                        },
                        {
                            key: 'insertBelow',
                            name: strings.tableMenuInsertBelow,
                            data: TableOperation.InsertBelow,
                            onClick: this.editTable,
                        },
                        {
                            key: 'insertLeft',
                            name: strings.tableMenuInsertLeft,
                            data: TableOperation.InsertLeft,
                            onClick: this.editTable,
                        },
                        {
                            key: 'insertRight',
                            name: strings.tableMenuInsertRight,
                            data: TableOperation.InsertRight,
                            onClick: this.editTable,
                        },
                    ],
                },
            },
            {
                key: 'delete',
                name: strings.tableMenuDelete,
                subMenuProps: {
                    items: [
                        {
                            key: 'deleteTable',
                            name: strings.tableMenuDeleteTable,
                            data: TableOperation.DeleteTable,
                            onClick: this.editTable,
                        },
                        {
                            key: 'deleteColumn',
                            name: strings.tableMenuDeleteColumn,
                            data: TableOperation.DeleteColumn,
                            onClick: this.editTable,
                        },
                        {
                            key: 'deleteRow',
                            name: strings.tableMenuDeleteRow,
                            data: TableOperation.DeleteRow,
                            onClick: this.editTable,
                        },
                    ],
                },
            },
            {
                key: 'merge',
                name: strings.tableMenuMerge,
                subMenuProps: {
                    items: [
                        {
                            key: 'mergeAbove',
                            name: strings.tableMenuMergeAbove,
                            data: TableOperation.MergeAbove,
                            onClick: this.editTable,
                        },
                        {
                            key: 'mergeBelow',
                            name: strings.tableMenuMergeBelow,
                            data: TableOperation.MergeBelow,
                            onClick: this.editTable,
                        },
                        {
                            key: 'mergeLeft',
                            name: strings.tableMenuMergeLeft,
                            data: TableOperation.MergeLeft,
                            onClick: this.editTable,
                        },
                        {
                            key: 'mergeRight',
                            name: strings.tableMenuMergeRight,
                            data: TableOperation.MergeRight,
                            onClick: this.editTable,
                        },
                    ],
                },
            },
            {
                key: 'split',
                name: strings.tableMenuSplit,
                subMenuProps: {
                    items: [
                        {
                            key: 'splitHorizontally',
                            name: strings.tableMenuSplitHorizontally,
                            data: TableOperation.SplitHorizontally,
                            onClick: this.editTable,
                        },
                        {
                            key: 'splitVertically',
                            name: strings.tableMenuSplitVertically,
                            data: TableOperation.SplitVertically,
                            onClick: this.editTable,
                        },
                    ],
                },
            },
            {
                key: 'style',
                name: strings.tableMenuStyle,
                subMenuProps: {
                    items: [
                        {
                            key: 'styleDefault',
                            name: strings.tableMenuStyleDefault,
                            data: 'Default',
                            onClick: this.formatTable,
                        },
                        {
                            key: 'styleGrid',
                            name: strings.tableMenuStyleGrid,
                            data: 'Grid',
                            onClick: this.formatTable,
                        },
                        {
                            key: 'styleLightLines',
                            name: strings.tableMenuStyleLightLines,
                            data: 'LightLines',
                            onClick: this.formatTable,
                        },
                        {
                            key: 'styleTwoTones',
                            name: strings.tableMenuStyleTwoTones,
                            data: 'TwoTones',
                            onClick: this.formatTable,
                        },
                        {
                            key: 'styleLightBands',
                            name: strings.tableMenuStyleLightBands,
                            data: 'LightBands',
                            onClick: this.formatTable,
                        },
                        {
                            key: 'styleClear',
                            name: strings.tableMenuStyleClear,
                            data: 'Clear',
                            onClick: this.formatTable,
                        },
                    ],
                },
            },
            {
                key: 'headers',
                name: strings.tableMenuHeaderRow,
                isChecked: this.props.viewState.hasHeaders,
                canCheck: true,
                onClick: this.setTableHeaders,
            },
        ];
    }

    private setTableHeaders = (ev: React.MouseEvent<HTMLElement>, item: IContextualMenuItem) => {
        this.onDismiss();
        this.props.plugin.setTableHeaders(this.props.viewState.hasHeaders);
    };

    private editTable = (ev: React.MouseEvent<HTMLElement>, item: IContextualMenuItem) => {
        this.onDismiss();
        let operation = item.data as TableOperation;
        this.props.plugin.editTable(operation);
    };

    private formatTable = (ev: React.MouseEvent<HTMLElement>, item: IContextualMenuItem) => {
        this.onDismiss();
        let formatName = item.data as string;
        this.props.plugin.formatTable(formatName);
    };
}
